import React, { useState, useEffect, useRef, useCallback } from "react";
import swal from "sweetalert";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Button,
  Col,
  Dropdown,
  DropdownButton,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";
import {
  AddIconOutline,
  ExportDownloadIcon,
  MenuDots,
  SearchIcon,
} from "../../components/SvgIcons";
import ExportClientCSVModalForm from "../../components/Modals/ExportClientCSVModal";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  constants,
  customStyles,
  rectSelectStyles,
  userTypeData,
  colorFilteringData,
} from "../../utils/constants";

import moment from "moment";
import { DeleteUser, GetUsers, UpdateUser } from "../../services/users.service";
import DataTable from "react-data-table-component";
import BarLoader from "react-spinners/BarLoader";
import { ClipLoader } from "react-spinners";
import {
  EmptyLocalStorage,
  GetAuthUser,
} from "../../services/auth/auth.service";
import ThemeModal from "../../components/ThemeModal";
import Select from "react-select";

const PAGE_SIZE = 30;
const TABLE_SCROLL_HEIGHT = "calc(100vh - 340px)";

const LazyUserAvatar = ({ src, alt, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src || constants.ClientImage);

  useEffect(() => {
    setLoaded(false);
    setCurrentSrc(src || constants.ClientImage);
  }, [src]);

  return (
    <div className="custom-img" style={{ position: "relative" }}>
      {!loaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "#e9ecef",
            borderRadius: "4px",
          }}
        />
      )}
      <img
        loading="lazy"
        decoding="async"
        onClick={onClick}
        onLoad={() => setLoaded(true)}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          setCurrentSrc(constants.ClientImage);
        }}
        src={currentSrc}
        className={"img-table img-thumbnail w-100 h-100"}
        alt={alt || ""}
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.2s ease" }}
      />
    </div>
  );
};

function Users() {
  let history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const current_user = GetAuthUser();
  const [exportClientCSVModalShow, setExportClientCSVModalShow] = useState(false);
  const [imageModal, setImageModal] = useState({
    url: "",
    status: false,
  });
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [selectedColorFiltering, setSelectedColorFiltering] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const requestIdRef = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const tableWrapperRef = useRef(null);

  const clearInput = () => {
    setKeyword("");
  };

  // Keep page from scrolling; only the table body should scroll
  useEffect(() => {
    document.documentElement.classList.add("users-list-no-page-scroll");
    document.body.classList.add("users-list-no-page-scroll");
    return () => {
      document.documentElement.classList.remove("users-list-no-page-scroll");
      document.body.classList.remove("users-list-no-page-scroll");
    };
  }, []);

  // Debounce search input only
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 500);
    return () => clearTimeout(handler);
  }, [keyword]);

  const mapUsersToRows = (data, startIndex = 0) => {
    return data.map((dt, index) => ({
      id: dt.id,
      user_short_id: dt.user_short_id,
      index: startIndex + index + 1,
      CategoryImg: dt.profile_image || constants.ClientImage,
      full_name: dt.full_name || "-",
      state: dt.state || "-",
      email: dt.email || "-",
      phone: dt.phone_number || "-",
      dob: moment(dt.dob).format("MM/DD/YYYY") || "-",
      spnNo: dt.license || "-",
      program: dt.program || "-",
      county: dt.county || "-",
      officer: dt.officer_name || "-",
      representative: dt.representative || "-",
      created_at: moment(dt.created_at).fromNow(),
      user_type: dt?.user_type,
      installation_certificate: dt?.installation_certificate,
      has_possible_temperred_report: dt?.has_possible_temperred_report,
      has_failed_report: dt?.has_failed_report,
      missed_tests_status: dt?.missed_tests_status,
    }));
  };

  const fetchUsers = useCallback(
    async (pageNum, { replace = false } = {}) => {
      const currentRequestId = ++requestIdRef.current;

      if (replace) {
        loadingRef.current = true;
        setLoading(true);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      } else {
        if (loadingMoreRef.current || loadingRef.current || !hasMoreRef.current) {
          return;
        }
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const result = await GetUsers(
          debouncedKeyword,
          constants.ROLES.ROLE_USER,
          pageNum,
          PAGE_SIZE,
          "",
          "",
          selectedUserType?.value,
          selectedColorFiltering?.value
        );

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        if (result.success) {
          if (result.data) {
            const total = result.total || 0;
            setTotalRows(total);
            pageRef.current = pageNum;
            setPage(pageNum);

            const mapped = mapUsersToRows(
              result.data,
              replace ? 0 : (pageNum - 1) * PAGE_SIZE
            );

            setRows((prev) => {
              const nextRows = replace ? mapped : [...(prev || []), ...mapped];
              return nextRows;
            });
            const approxLoaded = replace
              ? mapped.length
              : (pageNum - 1) * PAGE_SIZE + mapped.length;
            const more = approxLoaded < total && mapped.length > 0;
            hasMoreRef.current = more;
            setHasMore(more);
          } else if (replace) {
            setRows([]);
            hasMoreRef.current = false;
            setHasMore(false);
          }
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        if (error.response?.status == 401) {
          EmptyLocalStorage();
          history.push("/");
        } else {
          toast.error(error.response?.data?.message || "Failed to load users");
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          loadingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [debouncedKeyword, selectedUserType, selectedColorFiltering, history]
  );

  // Initial load + reload when search/filters change
  useEffect(() => {
    hasMoreRef.current = true;
    setHasMore(true);
    pageRef.current = 1;
    setPage(1);
    setRows([]);
    fetchUsers(1, { replace: true });
  }, [fetchUsers]);

  // Infinite scroll only inside the table body (not the whole page)
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const getScrollEl = () =>
      wrapper.querySelector(".rdt_TableBody") ||
      wrapper.querySelector('[data-testid="table-body"]') ||
      null;

    let scrollEl = getScrollEl();
    let tries = 0;

    const onScroll = () => {
      if (!scrollEl) return;
      if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) {
        return;
      }

      const remaining =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;

      if (remaining < 80) {
        const nextPage = pageRef.current + 1;
        fetchUsers(nextPage, { replace: false });
      }
    };

    const attach = () => {
      scrollEl = getScrollEl();
      if (!scrollEl) {
        if (tries < 20) {
          tries += 1;
          requestAnimationFrame(attach);
        }
        return;
      }
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    };

    attach();

    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener("scroll", onScroll);
      }
    };
  }, [fetchUsers, rows.length]);

  const refreshUsers = () => {
    hasMoreRef.current = true;
    setHasMore(true);
    pageRef.current = 1;
    setPage(1);
    fetchUsers(1, { replace: true });
  };

  const onUserDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willShip) => {
      if (willShip) {
        await DeleteUser(id)
          .then((result) => {
            if (result.success) {
              swal(result.message, {
                icon: "success",
              });
              refreshUsers();
            } else {
              toast.error(result.message);
            }
          })
          .catch((error) => {
            if (error.response.status == 401) {
              EmptyLocalStorage();
              history.push("/");
            } else {
              return toast.error(error.response.data.message);
            }
          });
      }
    });
  };

  const onLockoutUser = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to lockout this user? This will prevent the user from logging into their account.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willShip) => {
      if (willShip) {
        const data = {
          is_locked: true,
        };
        await UpdateUser(id, data)
          .then((result) => {
            if (result.success) {
              swal(result.message, {
                icon: "success",
              });
              refreshUsers();
            } else {
              toast.error(result.message);
            }
          })
          .catch((error) => {
            if (error.response.status == 401) {
              EmptyLocalStorage();
              history.push("/");
            } else {
              return toast.error(error.response.data.message);
            }
          });
      }
    });
  };

  const onMarkAsViewedMissedTests = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to mark this user as seen for missed tests?",
      icon: "warning",
      buttons: true,
      dangerMode: false,
    }).then(async (willShip) => {
      if (willShip) {
        const data = {
          missed_tests_status: false,
        };
        await UpdateUser(id, data)
          .then((result) => {
            if (result.success) {
              swal(result.message, {
                icon: "success",
              });
              refreshUsers();
            } else {
              toast.error(result.message);
            }
          })
          .catch((error) => {
            if (error.response.status == 401) {
              EmptyLocalStorage();
              history.push("/");
            } else {
              return toast.error(error.response.data.message);
            }
          });
      }
    });
  };

  const ModalForImage = () => {
    return (
      <>
        <div className={"modalBodyContent"} style={{ textAlign: "center" }}>
          <img
            style={{
              objectFit: "cover",
              objectPosition: "center",
              textAlign: "center",
            }}
            src={imageModal.url}
            className="img-fluid"
            alt=""
          />
        </div>
      </>
    );
  };

  const onEdit = (data) => {
    history.push("/manage-user/edit-user/" + data.id, {
      data: data,
    });
  };

  const onViewPage = (data) => {
    history.push("/manage-user/Userdetail/" + data.id);
  };

  const userActions = (data) => {
    return (
      <DropdownButton
        align="end"
        className={"actionDropdown"}
        id="dropdown-item-button"
        title={<MenuDots />}
      >
        <Dropdown.Item as="button" onClick={() => onViewPage(data)}>
          View
        </Dropdown.Item>

        {current_user &&
          (current_user.role === constants.ROLES.ROLE_ADMIN ||
            current_user.role === constants.ROLES.ROLE_AGENT) && (
            <React.Fragment>
              <Dropdown.Item as="button" onClick={() => onEdit(data)}>
                Edit
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={() => onUserDelete(data.id)}>
                Delete
              </Dropdown.Item>
              <Dropdown.Item as="button" onClick={() => onLockoutUser(data.id)}>
                Lockout User?
              </Dropdown.Item>
              {data?.missed_tests_status === true && (
                <Dropdown.Item
                  as="button"
                  onClick={() => onMarkAsViewedMissedTests(data.id)}
                >
                  Mark as Viewed (Missed Tests)
                </Dropdown.Item>
              )}
            </React.Fragment>
          )}
      </DropdownButton>
    );
  };

  const columns = [
    {
      id: "clientId",
      name: "S No.",
      width: "50px",
      selector: (rowData) => rowData.user_short_id,
    },
    {
      id: "CategoryImg",
      name: "Picture",
      width: "100px",
      selector: (rowData) => rowData.oid,
      format: (rowData) => (
        <LazyUserAvatar
          src={rowData.CategoryImg}
          alt=""
          onClick={() =>
            setImageModal({
              url: rowData.CategoryImg,
              status: true,
            })
          }
        />
      ),
    },
    {
      id: "full_name",
      name: "Full Name",
      width: "200px",
      selector: (rowData) => rowData.full_name,
      format: (rowData) => (
        <NavLink
          className="text-dark text-decoration-none d-flex"
          to={{
            pathname: `/manage-user/Userdetail/${rowData.id}`,
            state: {
              page: page,
            },
          }}
        >
          {rowData.full_name}
        </NavLink>
      ),
    },
    {
      id: "dob",
      name: "Date of Birth",
      selector: (rowData) => rowData.dob,
    },
    {
      id: "dob",
      name: "Case No. OR SPN",
      selector: (rowData) => rowData.spnNo,
    },
    {
      id: "dob",
      name: "Program",
      selector: (rowData) => (
        <div className="text-uppercase">{rowData.program}</div>
      ),
    },
    {
      id: "dob",
      name: "County",
      selector: (rowData) => rowData.county,
    },
    {
      id: "email",
      name: "Officer",
      selector: (rowData) => rowData.officer,
    },
    {
      id: "user_type",
      name: "Device",
      selector: (rowData) =>
        Array.isArray(rowData.user_type) && rowData.user_type.length > 0
          ? rowData.user_type
              .map((u_type) => (u_type == 10 ? "Portable" : "Interlock"))
              .join(", ")
          : "",
    },
    {
      id: "action",
      name: "Action",
      cell: (rowData) => userActions(rowData),
    },
  ];

  const onChangeUserType = (selectedOption) => {
    setSelectedUserType(selectedOption);
  };

  const onChangeColorFiltering = (selectedOption) => {
    setSelectedColorFiltering(selectedOption);
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.has_failed_report,
      style: {
        backgroundColor: "lightcoral",
        "&:hover": {
          backgroundColor: "white",
        },
      },
    },
    {
      when: (row) =>
        row.has_failed_report === false && row.has_possible_temperred_report,
      style: {
        backgroundColor: "lightyellow",
        "&:hover": {
          backgroundColor: "white",
        },
      },
    },
    {
      when: (row) => row.missed_tests_status === true,
      style: {
        backgroundColor: "#FFD580",
        "&:hover": {
          backgroundColor: "white",
        },
      },
    },
  ];

  return (
    <div className={"usersPage users-page-fixed"}>
      <h3 className="page-heading">Manage Users</h3>
      <hr />
      <div className={"customOrderFilters users-page-filters"}>
        <Row>
          <Col xs={12} sm={12} md={4} lg={5} xl={5}>
            <InputGroup className="mb-3 search-group">
              <InputGroup.Text id="basic-addon1">
                <SearchIcon />
              </InputGroup.Text>
              <FormControl
                placeholder="Search"
                className="custom-search"
                aria-label="Search"
                aria-describedby="Search"
                maxLength={"255"}
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                }}
                style={{
                  borderRight: keyword ? "none" : "1px solid #ced4da",
                  borderTopRightRadius: keyword ? "0" : "5px",
                  borderBottomRightRadius: keyword ? "0" : "5px",
                }}
              />
              {keyword && (
                <InputGroup.Text
                  className="cross-icon"
                  id="basic-addon2"
                  onClick={clearInput}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 6.48828L18 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18 6.48828L6 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InputGroup.Text>
              )}
            </InputGroup>
          </Col>
          <Col xs={12} sm={12} md={8} lg={7} xl={7}>
            <Row className="justify-content-end">
              <Col xs={12} sm={12} md={3} lg={3} xl={3}>
                <Select
                  name="userTypeSelect"
                  styles={rectSelectStyles}
                  onChange={onChangeColorFiltering}
                  options={colorFilteringData}
                  isSearchable={false}
                  placeholder={"Filter by Tests"}
                  className="react-select-container mb-3"
                  classNamePrefix="react-select"
                  isClearable
                />
              </Col>
              <Col xs={12} sm={12} md={3} lg={3} xl={3}>
                <Select
                  name="userTypeSelect"
                  styles={rectSelectStyles}
                  onChange={onChangeUserType}
                  options={userTypeData}
                  isSearchable={false}
                  placeholder={"Filter Users"}
                  className="react-select-container mb-3"
                  classNamePrefix="react-select"
                  isClearable
                />
              </Col>
              {(current_user.role == constants.ROLES.ROLE_ADMIN ||
                current_user.role == constants.ROLES.ROLE_AGENT) && (
                <Col xs={12} sm={12} md={3} lg={3} xl={3}>
                  <Button
                    className={"btn btn-green-theme h40 w-100"}
                    onClick={() => setExportClientCSVModalShow(true)}
                  >
                    <ExportDownloadIcon /> Export CSV
                  </Button>
                </Col>
              )}
              {(current_user.role == constants.ROLES.ROLE_ADMIN ||
                current_user.role == constants.ROLES.ROLE_AGENT) && (
                <Col xs={12} sm={12} md={3} lg={3} xl={3}>
                  <Link to={"/manage-user/add-user"}>
                    <Button className={"btn btn-green-theme h40 w-100"}>
                      <AddIconOutline /> Add New
                    </Button>
                  </Link>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </div>
      <div
        className="theme-dataGrid products-dataGrid custom-table-border user-table users-infinite-table mt-3"
        ref={tableWrapperRef}
      >
        <DataTable
          columns={columns}
          data={rows}
          progressPending={loading}
          customStyles={customStyles}
          selectableRows={false}
          striped
          highlightOnHover
          pagination={false}
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight={TABLE_SCROLL_HEIGHT}
          progressComponent={
            <BarLoader
              color={"#961b1a"}
              loading={loading}
              css={"marginTop: 10px"}
              height={"4"}
              width={"100%"}
            />
          }
          conditionalRowStyles={conditionalRowStyles}
        />
        {loadingMore && (
          <div
            className="users-load-more-indicator"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "12px",
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              margin: "0 12px",
              padding: "12px 16px",
              border: "1px solid #f0d0d0",
              borderRadius: "8px",
              background: "rgba(255, 255, 255, 0.98)",
              color: "#961b1a",
              fontSize: "13px",
              fontWeight: 500,
              boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
            }}
          >
            <ClipLoader color="#961b1a" size={22} />
            <span>Loading more users...</span>
          </div>
        )}
        {!loading && !loadingMore && rows.length > 0 && !hasMore && (
          <div
            className="users-load-more-end"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "12px",
              zIndex: 4,
              textAlign: "center",
              margin: "0 12px",
              padding: "10px 16px",
              color: "#6c757d",
              fontSize: "13px",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              background: "#fff",
            }}
          >
            Showing all {totalRows} users
          </div>
        )}
      </div>
      <ThemeModal
        title={""}
        size={"md"}
        content={<ModalForImage />}
        show={imageModal.status}
        onHide={() =>
          setImageModal({
            url: "",
            status: false,
          })
        }
      />

      <ThemeModal
        title={"Export Clients CSV"}
        size={"md"}
        content={<ExportClientCSVModalForm />}
        show={exportClientCSVModalShow}
        onHide={() => setExportClientCSVModalShow(false)}
      />
    </div>
  );
}

export default Users;
